import Phaser from "phaser";
import { bakeTexture, OUTLINE, roundedRectPath } from "./bake.ts";
import { css, hash01 } from "./color.ts";

export const PEDAL_SIZE = { plate: 118, post: 46 };

/** Brushed-metal pedal plate with six recessed holes, an engraved label and a post below. */
export function ensurePedalTexture(scene: Phaser.Scene, label: string): string {
  const P = PEDAL_SIZE.plate;
  const W = P + 8;
  const H = P + PEDAL_SIZE.post + 6;
  return bakeTexture(scene, `pedal-${label}`, W, H, (ctx) => {
    const outline = css(OUTLINE);
    const x = 4;
    const y = 3;

    // Post (behind the plate).
    const postW = 26;
    const postX = W / 2 - postW / 2;
    const post = ctx.createLinearGradient(postX, 0, postX + postW, 0);
    post.addColorStop(0, css(0x5d6066));
    post.addColorStop(0.45, css(0xd5d8dd));
    post.addColorStop(1, css(0x55585d));
    ctx.fillStyle = post;
    ctx.fillRect(postX, y + P - 10, postW, PEDAL_SIZE.post + 10);
    ctx.strokeStyle = outline;
    ctx.lineWidth = 2.5;
    ctx.strokeRect(postX, y + P - 10, postW, PEDAL_SIZE.post + 12);

    // Plate.
    roundedRectPath(ctx, x, y, P, P, 12);
    const metal = ctx.createLinearGradient(x, y, x + P, y + P);
    metal.addColorStop(0, css(0xf7f8f9));
    metal.addColorStop(0.5, css(0xc3c6cb));
    metal.addColorStop(1, css(0x8e9298));
    ctx.fillStyle = metal;
    ctx.fill();

    ctx.save();
    roundedRectPath(ctx, x, y, P, P, 12);
    ctx.clip();
    for (let i = 0; i < 70; i++) {
      const sy = y + hash01(i, 3) * P;
      ctx.strokeStyle = css(hash01(i, 4) > 0.5 ? 0xffffff : 0x000000, 0.06 + hash01(i, 5) * 0.06);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, sy);
      ctx.lineTo(x + P, sy + 2);
      ctx.stroke();
    }
    ctx.restore();

    // Bevel: light top-left, dark bottom-right, then the outline.
    ctx.lineWidth = 3;
    ctx.strokeStyle = css(0xffffff, 0.8);
    ctx.beginPath();
    ctx.moveTo(x + 4, y + P - 12);
    ctx.lineTo(x + 4, y + 12);
    ctx.arcTo(x + 4, y + 4, x + 12, y + 4, 8);
    ctx.lineTo(x + P - 12, y + 4);
    ctx.stroke();
    ctx.strokeStyle = css(0x000000, 0.25);
    ctx.beginPath();
    ctx.moveTo(x + P - 4, y + 12);
    ctx.lineTo(x + P - 4, y + P - 12);
    ctx.arcTo(x + P - 4, y + P - 4, x + P - 12, y + P - 4, 8);
    ctx.lineTo(x + 12, y + P - 4);
    ctx.stroke();
    roundedRectPath(ctx, x, y, P, P, 12);
    ctx.strokeStyle = outline;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Recessed holes.
    const holeR = 13;
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const hx = x + P / 2 + (col - 1) * 34;
        const hy = y + 30 + row * 32;
        const hole = ctx.createRadialGradient(hx, hy - 3, 2, hx, hy, holeR);
        hole.addColorStop(0, css(0x0d0d0d));
        hole.addColorStop(1, css(0x3c3e42));
        ctx.fillStyle = hole;
        ctx.beginPath();
        ctx.arc(hx, hy, holeR, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = outline;
        ctx.stroke();
        ctx.strokeStyle = css(0xffffff, 0.7);
        ctx.beginPath();
        ctx.arc(hx, hy, holeR + 1.5, Math.PI * 0.15, Math.PI * 0.85);
        ctx.stroke();
      }
    }

    // Engraved label.
    ctx.font = `900 21px "Arial Black", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = css(0xffffff, 0.7);
    ctx.fillText(label, x + P / 2, y + P - 20 + 1.2);
    ctx.fillStyle = css(0x34373c);
    ctx.fillText(label, x + P / 2, y + P - 20);
  });
}

export const GAUGE_START = Math.PI * 0.8;
export const GAUGE_END = Math.PI * 2.2;

/** Analog dial face: white arc with ticks, a red zone at the top end and a label. */
export function ensureGaugeTexture(scene: Phaser.Scene, label: string, radius: number): string {
  const size = radius * 2 + 12;
  return bakeTexture(scene, `gauge-${label}-${radius}`, size, size, (ctx) => {
    const c = size / 2;
    ctx.translate(c, c);
    const r = radius;
    const outline = css(OUTLINE);

    ctx.fillStyle = css(0x000000, 0.22);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();

    const arc = (from: number, to: number, width: number, style: string) => {
      ctx.strokeStyle = style;
      ctx.lineWidth = width;
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.arc(0, 0, r - 6, from, to);
      ctx.stroke();
    };
    arc(GAUGE_START - 0.04, GAUGE_END + 0.04, 10, outline);
    arc(GAUGE_START, GAUGE_END, 5, css(0xffffff));
    const redFrom = GAUGE_START + (GAUGE_END - GAUGE_START) * 0.78;
    arc(redFrom, GAUGE_END, 5, css(0xc62f2f));

    const ticks = 8;
    ctx.lineCap = "round";
    for (let i = 0; i <= ticks; i++) {
      const a = GAUGE_START + ((GAUGE_END - GAUGE_START) * i) / ticks;
      const inner = r - (i % 2 === 0 ? 22 : 17);
      const outer = r - 11;
      for (const [width, style] of [
        [5, outline],
        [2.4, css(0xffffff)],
      ] as const) {
        ctx.strokeStyle = style;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
        ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
        ctx.stroke();
      }
    }

    ctx.font = `900 ${Math.round(r * 0.27)}px "Arial Black", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = 4;
    ctx.strokeStyle = outline;
    ctx.strokeText(label, 0, r * 0.62);
    ctx.fillStyle = css(0xffffff);
    ctx.fillText(label, 0, r * 0.62);
  });
}

export const FUEL_BAR_SIZE = { width: 150, height: 22 };

/** Glossy lime fill for the fuel bar (cropped to the current fuel level). */
export function ensureFuelBarFillTexture(scene: Phaser.Scene): string {
  const { width: W, height: H } = FUEL_BAR_SIZE;
  return bakeTexture(scene, "fuel-bar-fill", W, H, (ctx) => {
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, css(0xd8ff5a));
    grad.addColorStop(0.5, css(0xa8e81e));
    grad.addColorStop(1, css(0x76b80c));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = css(0xffffff, 0.35);
    ctx.fillRect(0, 2, W, 4);
  });
}

/** White two-bar pause icon with a dark outline. */
export function ensurePauseIconTexture(scene: Phaser.Scene): string {
  return bakeTexture(scene, "pause-icon", 44, 48, (ctx) => {
    for (const bx of [6, 25]) {
      roundedRectPath(ctx, bx, 4, 13, 40, 4);
      const grad = ctx.createLinearGradient(bx, 0, bx + 13, 0);
      grad.addColorStop(0, css(0xffffff));
      grad.addColorStop(1, css(0xc9ccd1));
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = css(OUTLINE);
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  });
}
